import React from 'react';
// @ts-ignore - Remotion型定義の互換性問題の暫定対応（既存remotionコンポジション踏襲）
import { AbsoluteFill, Audio, Img, Sequence, Video, interpolate, useCurrentFrame, useVideoConfig } from 'remotion';
import { CaptionSegment } from '../../services/api/songAdService';
import { SongAdCompositionScene, SongAdCtaCard } from './types';
import { CAPTION_POSITION_CSS, CAPTION_STYLE_CSS, computeCaptionFontSize } from './captionPresets';
import { invalidateStableMediaUrl } from './stableMediaUrl';

/**
 * 歌広告のRemotion Composition（spec §12）。プレビュー/詳細編集の両方から@remotion/playerで使う。
 *
 * props は timeline JSON から派生させた「渡された配列をそのまま等分して並べる」だけの
 * 純粋な形にしている（派生ロジックは deriveScenes.ts。skipシーンの除外・順序解決はそこで完結）。
 *
 * - 映像トラック: still=Ken Burns（Imgをscale補間） / clip=Video。シーン枠（開始終了秒）は
 *   deriveScenes.ts が sceneTiming.computeSceneWindows で歌詞行数比配分したものを使う
 *   （timeline JSON自体にはシーン毎の開始終了を保持しない。scene_order + motion が正で、
 *   窓はbackendのSongAdRenderPlannerと同じロジックでその都度導出する）
 * - テロップトラック: CaptionSegment（秒単位のstart/end）を4プリセットで描画（1行ずつ描画。
 *   \n 区切りの複数行はbackendのCaptionTextFormatterが決めた折り返し。v4 F9）
 * - CTAカード（v4 D/F9）: cta prop があれば末尾に最前面で描画（deriveCtaCard が算出した
 *   trim済みシーン枠+CTA窓をそのまま使う。backendのSongAdRenderPlanner::applyCtaと同じ規則）
 * - 楽曲: Audioで1トラック
 * - staticFileは使わずURL直参照（S3 presigned/CDN。v3アーキ決定）。呼び出し元（EditorStep/
 *   PreviewStep）は `stabilizeCompositionMedia()`（v5 k）でURL文字列値を安定化してから渡す
 *   前提。ここでは失効時（`error` イベント）にキャッシュを破棄するだけ（`invalidateStableMediaUrl`）
 */
export interface SongAdCompositionProps {
  scenes: SongAdCompositionScene[];
  captions: CaptionSegment[];
  audioUrl: string | null;
  /** 末尾CTAカード（v4 D/B5/F9）。deriveCtaCard の出力をそのまま渡す。無ければ描画しない */
  cta?: SongAdCtaCard | null;
}

const SceneVisual: React.FC<{ scene: SongAdCompositionScene; durationInFrames: number }> = ({
  scene,
  durationInFrames,
}) => {
  const frame = useCurrentFrame();
  const scale = interpolate(frame, [0, Math.max(1, durationInFrames)], [1, 1.08], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  if (scene.motionMode === 'clip' && scene.videoUrl) {
    return (
      <AbsoluteFill>
        <Video
          src={scene.videoUrl}
          muted
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          // v5 k: presigned URLの失効対策。読み込み失敗時はキャッシュを破棄し、
          // 次回 stabilizeCompositionMedia() 呼び出しで最新URLへ更新されるようにする
          onError={() => invalidateStableMediaUrl(scene.videoUrl)}
          // v7: 音声クラックル対策とペアの措置（下のAudioコメント参照）。muted なので聞こえには
          // 影響しないが、映像トラックも同じ理由で無音のまま突然コマ飛びするのを防ぐ
          pauseWhenBuffering
        />
      </AbsoluteFill>
    );
  }

  if (scene.imageUrl) {
    return (
      <AbsoluteFill style={{ overflow: 'hidden' }}>
        <Img
          src={scene.imageUrl}
          style={{ width: '100%', height: '100%', objectFit: 'cover', transform: `scale(${scale})` }}
          onError={() => invalidateStableMediaUrl(scene.imageUrl)}
        />
      </AbsoluteFill>
    );
  }

  return <AbsoluteFill style={{ backgroundColor: '#1D1927' }} />;
};

const CaptionOverlay: React.FC<{ caption: CaptionSegment; fps: number; width: number; height: number }> = ({
  caption,
  fps,
  width,
  height,
}) => {
  const frame = useCurrentFrame();
  const startFrame = Math.round(caption.start * fps);
  const endFrame = Math.round(caption.end * fps);
  if (frame < startFrame || frame >= endFrame) return null;

  // v6 F3: ASS（書き出し）と同じ式で計算し、プレビューと書き出しのフォントサイズを一致させる
  const fontSize = computeCaptionFontSize(width, height, caption.size);

  // v4 F9: backendのCaptionTextFormatter（B3）が \n 区切りで最大2行に整形済みのテキストを送ってくる。
  // 1行ずつ独立した要素として描画する（'pop' 等の行ごとの背景プリセットが、2行分をまとめて
  // 1つの巨大な吹き出しに描画してしまわないようにするため。書き出し側ASSも \N で行分割して
  // 同じ見た目にする＝ここが「行ごと」に割るのがプレビュー/書き出し一致の要）
  const lines = caption.text.split('\n').filter((line) => line.length > 0);

  return (
    <div
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: fontSize * 0.15,
        padding: '0 7%',
        pointerEvents: 'none',
        ...CAPTION_POSITION_CSS[caption.position],
      }}
    >
      {lines.map((line, index) => (
        <span
          key={index}
          style={{
            display: 'inline-block',
            fontSize,
            lineHeight: 1.4,
            textAlign: 'center',
            whiteSpace: 'pre-wrap',
            ...CAPTION_STYLE_CSS[caption.style],
          }}
        >
          {line}
        </span>
      ))}
    </div>
  );
};

/**
 * 末尾CTAカード（v4 D/B5/F9/F11）。backend `SongAdCtaCardBuilder`/`SongAdRenderPlanner::applyCta` と
 * 同じ規則: 最終シーン画像をぼかし+減光した背景の上に、ロゴ（あれば）+文言を中央表示する。
 * ロゴは `project.cta.logo_url`（v4 F11でAPI契約に追加。backendがKnowledgeの商品画像
 * category=brand_logoから解決済みのURL）をそのまま使う。無ければ文言のみ。
 */
// CTAカードの文言サイズはテロップ（r）のスコープ外なので、v6 F3のwidth考慮式には寄せず、
// 旧 CAPTION_SIZE_RATIO.lg（0.058）と同じ値を維持して見た目を変えない
const CTA_CARD_FONT_HEIGHT_RATIO = 0.058;

const CtaCardOverlay: React.FC<{ cta: SongAdCtaCard; width: number; height: number }> = ({ cta, width, height }) => {
  const fontSize = Math.max(16, Math.round(height * CTA_CARD_FONT_HEIGHT_RATIO * 1.3));
  const logoWidth = Math.round(width * 0.45); // 画面幅の40〜50%程度

  return (
    <AbsoluteFill>
      {cta.backgroundImageUrl ? (
        <Img
          src={cta.backgroundImageUrl}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            // backendの boxblur + eq(brightness=-0.18, saturation=0.55) の近似
            filter: 'blur(14px) brightness(0.82) saturate(0.55)',
            transform: 'scale(1.06)', // ぼかしで縁が透けるのを防ぐ
          }}
          onError={() => invalidateStableMediaUrl(cta.backgroundImageUrl)}
        />
      ) : (
        <AbsoluteFill style={{ backgroundColor: '#1D1927' }} />
      )}
      <AbsoluteFill style={{ backgroundColor: 'rgba(0,0,0,.25)' }} />
      <AbsoluteFill
        style={{
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 10%',
          gap: fontSize * 0.6,
        }}
      >
        {cta.logoUrl && (
          <Img
            src={cta.logoUrl}
            style={{ width: logoWidth, maxHeight: height * 0.28, objectFit: 'contain' }}
            onError={() => invalidateStableMediaUrl(cta.logoUrl)}
          />
        )}
        <span
          style={{
            display: 'inline-block',
            fontSize,
            fontWeight: 700,
            lineHeight: 1.4,
            textAlign: 'center',
            color: '#fff',
            letterSpacing: '.04em',
            textShadow: '0 2px 14px rgba(0,0,0,.6), 0 0 3px rgba(0,0,0,.5)',
          }}
        >
          {cta.text}
        </span>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

const SongAdComposition: React.FC<SongAdCompositionProps> = ({ scenes, captions, audioUrl, cta }) => {
  const { fps, durationInFrames, width, height } = useVideoConfig();

  return (
    <AbsoluteFill style={{ backgroundColor: '#000' }}>
      {scenes.map((scene) => {
        const from = Math.max(0, Math.round(scene.startSec * fps));
        const to = Math.min(durationInFrames, Math.round(scene.endSec * fps));
        const duration = to - from;
        if (duration <= 0) return null;
        return (
          <Sequence key={scene.id} from={from} durationInFrames={duration}>
            <SceneVisual scene={scene} durationInFrames={duration} />
          </Sequence>
        );
      })}

      {captions.map((caption) => (
        <CaptionOverlay key={caption.id} caption={caption} fps={fps} width={width} height={height} />
      ))}

      {/* CTAカードは最後（＝最前面）に描画し、被っているシーン映像やテロップを覆い隠す
          （backendが末尾テロップのendをctaStartでクリップするのと同じ「CTA中はCTAのみ見える」状態を、
          プレビューでは trimしたシーン枠 + 最前面レイヤーの組み合わせで再現する） */}
      {cta && (
        <Sequence
          from={Math.max(0, Math.round(cta.start * fps))}
          durationInFrames={Math.max(1, Math.round((cta.end - cta.start) * fps))}
        >
          <CtaCardOverlay cta={cta} width={width} height={height} />
        </Sequence>
      )}

      {audioUrl && (
        <Audio
          src={audioUrl}
          onError={() => invalidateStableMediaUrl(audioUrl)}
          // v7: 「音声がガビガビ」対策。remotion/dist/.../use-media-playback.js の同期ロジックは
          // 毎フレーム「あるべき再生位置(frame基準)」と実際のHTMLAudioElement/rVFCの再生位置の差分を見て、
          // 既定の許容量（約0.65秒。acceptableTimeShiftInSeconds未指定時のデフォルト）を超えると
          // audio.currentTime を強制的に書き換える「ハードシーク」を行う。これは波形の不連続点になり
          // 耳には「プツッ」というノイズとして聞こえる。デフォルトでは pauseWhenBuffering が false のため、
          // 音声側が waiting（バッファリング）でネイティブに詰まっても再生クロック（RAF駆動）は止まらず
          // 先に進み続け、乖離が閾値を超えるたびにこのハードシークが発生する
          // （v6 e2eで観測された「約3.6秒間隔のwaiting→seeked」はこのパターンと一致）。
          // pauseWhenBuffering を有効にすると、音声が waiting を発火した瞬間にPlayer全体の
          // 再生クロックも一緒に止めて音声のcanplayを待つため、乖離が閾値まで積み上がる前に
          // 追従でき、ハードシーク自体の発生頻度を下げられる（Remotion公式のバッファリング時の
          // 音声/映像同期崩れに対する標準的な対処。node_modules/remotion/dist/esm/index.mjs の
          // useMediaBuffering 参照）
          pauseWhenBuffering
        />
      )}
    </AbsoluteFill>
  );
};

export default SongAdComposition;
